/**
 * Employee Self-Service Hub — /me (M3X.UIPOLISH PR 18 ★ KEYSTONE).
 *
 * Per spec §1.C.2: a polished employee landing page mirroring
 * PeopleHub's design. Removed from sidebar per Q1A; reachable via
 * top-bar avatar dropdown "My Hub" (PR 17) + direct URL.
 *
 * Closes §0.4 — the prior /me path showed developer-instruction
 * text ("Append ?employee_id=<uuid>"). This page is role-agnostic;
 * the existing self-service sub-pages already handle employee-vs-HR
 * gating.
 */

import { useTranslation } from "react-i18next";
import {
  Banknote,
  Calendar,
  Clock,
  FileText,
  Gauge,
  Sparkles,
  User as UserIcon,
} from "lucide-react";

import { PageHeader, SettingsEntryCard, SettingsSection } from "../components/ui";

export function MyHubPage() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("my_hub.title", "My Hub")}
        subtitle={t(
          "my_hub.subtitle",
          "Your payslips, leaves, attendance, and documents in one place.",
        )}
      />

      <SettingsSection
        title={t("my_hub.section_services", "My services")}
      >
        <SettingsEntryCard
          icon={Calendar}
          title={t("my_hub.svc_leaves_title", "Leaves")}
          description={t(
            "my_hub.svc_leaves_desc",
            "Request leave, view balances, see your history.",
          )}
          to="/me/leaves"
          tone="green"
        />
        <SettingsEntryCard
          icon={Banknote}
          title={t("my_hub.svc_payment_title", "Payment Hub")}
          description={t(
            "my_hub.svc_payment_desc",
            "Payslips, loans, salary certificates, year-end statements.",
          )}
          to="/me/loans"
          tone="indigo"
        />
        <SettingsEntryCard
          icon={Gauge}
          title={t("my_hub.svc_eos_title", "End of Service")}
          description={t(
            "my_hub.svc_eos_desc",
            "Resignation, retirement, and final settlement.",
          )}
          to="/me/end-of-service"
          tone="purple"
        />
        <SettingsEntryCard
          icon={Clock}
          title={t("my_hub.svc_attendance_title", "Attendance")}
          description={t(
            "my_hub.svc_attendance_desc",
            "Clock-in history, excuses, overtime, work-from-home.",
          )}
          to="/me/attendance"
          tone="teal"
        />
        <SettingsEntryCard
          icon={FileText}
          title={t("my_hub.svc_documents_title", "Documents")}
          description={t(
            "my_hub.svc_documents_desc",
            "Your contract, IDs, year-end statement, salary certificate.",
          )}
          to="/me/loans"
          tone="amber"
        />
        <SettingsEntryCard
          icon={UserIcon}
          title={t("my_hub.svc_profile_title", "Profile")}
          description={t(
            "my_hub.svc_profile_desc",
            "Bank details, contact info, emergency contacts.",
          )}
          to="/me/loans"
          tone="neutral"
        />
      </SettingsSection>

      <section
        className="flex items-start gap-3 rounded-lg border border-ai-subtle bg-ai-bg p-4 text-sm text-text-secondary"
        aria-label={t("my_hub.khidmat_hint_label", "Khidmat tip")}
      >
        <Sparkles
          size={16}
          className="mt-0.5 shrink-0 text-ai-deep"
          aria-hidden
        />
        <div>
          <p className="font-medium text-text-primary">
            {t("my_hub.khidmat_hint_title", "Ask Khidmat anything")}
          </p>
          <p className="mt-0.5 leading-relaxed">
            {t(
              "my_hub.khidmat_hint_body",
              "Khidmat is your personal AI assistant for HR. Ask via WhatsApp or in the chat panel — leave requests, salary questions, document downloads — anytime.",
            )}
          </p>
        </div>
      </section>
    </div>
  );
}

export default MyHubPage;
